import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Send, Target, Users, MapPin,
  DollarSign, Sparkles, ShieldCheck, Package, ArrowRight, TrendingUp,
} from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import MatchScore from "@/components/MatchScore";
import ScoreBadge from "@/components/ScoreBadge";

export default function BriefPublic() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [myProjects, setMyProjects] = useState([]);
  const [scores, setScores] = useState([]);
  const [projId, setProjId] = useState("");
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/briefs-public/${id}`);
        setData(data);
      } catch (e) {
        toast.error(t("brief_public.not_found"));
        nav("/explorar");
      }
    })();
  }, [id, nav, t]);

  useEffect(() => {
    if (!user || user.role !== "creator") return;
    (async () => {
      const [{ data: projs }, { data: sc }] = await Promise.all([
        api.get("/api/projects/mine"),
        api.get(`/api/briefs-public/${id}/pitch-scores`).catch(() => ({ data: [] })),
      ]);
      setMyProjects(projs);
      setScores(sc);
      // Auto-select best-scoring project by default
      const best = sc[0];
      if (best) setProjId(best.project_id);
      else if (projs.length > 0) setProjId(projs[0].id);
    })();
  }, [user, id]);

  const currentScore = scores.find((s) => s.project_id === projId);

  const send = async () => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "creator") { toast.error(t("brief_public.only_creators_can_pitch")); return; }
    if (!projId) { toast.error(t("brief_public.select_project_error")); return; }
    try {
      await api.post(`/api/briefs-public/${id}/pitch`, { project_id: projId, message });
      toast.success(t("brief_public.pitch_sent"));
      setOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(t("brief_public.pitch_error"));
    }
  };

  if (!data) return (
    <div className="marketing-shell min-h-screen"><MarketingNav /><div className="mx-auto max-w-6xl px-6 py-20 text-white/60">{t("brief_public.loading")}</div></div>
  );

  const { brief, sponsor, brand } = data;
  const budget = (brief.budget_min || brief.budget_max)
    ? `${brief.currency} ${(brief.budget_min || 0).toLocaleString()} – ${brief.budget_max ? brief.budget_max.toLocaleString() : t("brief_public.budget_open")}`
    : t("brief_public.budget_negotiate");

  const acceptsLine = brief.accepts_products && brief.accepts_services
    ? t("brief_public.accepts_both")
    : brief.accepts_products
      ? t("brief_public.accepts_products")
      : brief.accepts_services
        ? t("brief_public.accepts_services")
        : null;

  return (
    <div className="marketing-shell min-h-screen">
      <MarketingNav />
      <section className="relative">
        <div className="mx-auto max-w-6xl px-6 pt-12 pb-16">
          <button onClick={() => nav(-1)} className="text-white/70 inline-flex items-center gap-2 hover:text-gold mb-8" data-testid="button-back">
            <ArrowLeft size={16} /> {t("brief_public.back")}
          </button>

          <p className="eyebrow text-gold">{t("brief_public.eyebrow")}</p>
          <h1 className="font-display text-white text-5xl md:text-6xl tracking-tight mt-4 max-w-3xl leading-tight">
            {brief.title}
          </h1>

          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <span className="glass-card rounded-full px-4 py-2 text-sm text-white inline-flex items-center gap-2">
              <Building2 size={14} className="text-gold" />
              {sponsor?.hidden ? t("brief_public.sponsor_anon") : (sponsor?.commercial_name || t("brief_public.sponsor_default"))}
              {sponsor?.hidden && <ShieldCheck size={12} className="text-gold" />}
            </span>
            {brand && !brief.hidden_identity && brand.name && (
              <span className="glass-card rounded-full px-4 py-2 text-sm text-white inline-flex items-center gap-2">
                <Package size={14} className="text-gold" /> {brand.name}
              </span>
            )}
            {brief.objective && (
              <span className="glass-card rounded-full px-4 py-2 text-sm text-white inline-flex items-center gap-2">
                <Target size={14} className="text-gold" /> {brief.objective}
              </span>
            )}
          </div>

          {brief.owner_id && (
            <div className="mt-6 flex items-center gap-3">
              <span className="text-white/60 text-xs uppercase tracking-widest">{t("brief_public.reputation_label")}</span>
              <ScoreBadge userId={brief.owner_id} theme="dark" size="md" />
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-pitch" className="gold-cta rounded-full px-6 py-5 font-semibold">
                  <Send size={16} className="mr-2" /> {t("brief_public.pitch_button")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("brief_public.pitch_dialog_title")}</DialogTitle>
                </DialogHeader>
                {!user ? (
                  <div className="py-4">
                    <p className="text-slate-500">{t("brief_public.need_creator_account")}</p>
                    <Button className="mt-4 bg-petroleum text-white hover:bg-petroleum/90" onClick={() => nav("/registro?role=creator")}>
                      {t("brief_public.create_creator_account")}
                    </Button>
                  </div>
                ) : user.role !== "creator" ? (
                  <p className="text-slate-500">{t("brief_public.only_creators_pitch")}</p>
                ) : myProjects.length === 0 ? (
                  <div className="py-4">
                    <p className="text-slate-500">{t("brief_public.need_published_project")}</p>
                    <Button className="mt-4 bg-petroleum text-white hover:bg-petroleum/90" onClick={() => nav("/dashboard/proyectos/nuevo")}>
                      {t("brief_public.create_project")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-sm text-slate-600 mb-2">{t("brief_public.select_best_project")}</p>
                      <Select value={projId} onValueChange={setProjId}>
                        <SelectTrigger data-testid="select-pitch-project"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {myProjects.map((p) => {
                            const s = scores.find((x) => x.project_id === p.id);
                            return (
                              <SelectItem key={p.id} value={p.id}>
                                <span className="inline-flex items-center gap-2">
                                  <span>{p.title}</span>
                                  {s && <span className={`text-[10px] rounded-full px-2 py-0.5 ${s.score >= 70 ? "bg-emerald-100 text-emerald-700" : s.score >= 40 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{s.score}%</span>}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    {currentScore && (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="pitch-score-preview">
                        <div className="flex items-start gap-4">
                          <MatchScore score={currentScore.score} size="md" />
                          <div className="flex-1 min-w-0">
                            <p className="eyebrow text-gold text-[10px]">{t("brief_public.compatibility")}</p>
                            <p className="text-sm text-petroleum font-semibold mt-0.5">
                              {currentScore.score >= 70 ? t("brief_public.match_excellent") : currentScore.score >= 40 ? t("brief_public.match_partial") : t("brief_public.match_low")}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {(currentScore.reasons || []).slice(0, 4).map((r, i) => (
                                <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-white text-slate-600 border border-slate-200 uppercase tracking-widest">
                                  {r.label}: {r.score}%
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <Textarea
                      className="mt-4"
                      rows={5}
                      placeholder={t("brief_public.pitch_message_placeholder")}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      data-testid="textarea-pitch-message"
                    />
                  </>
                )}
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                  {user && user.role === "creator" && myProjects.length > 0 && (
                    <Button onClick={send} data-testid="button-send-pitch" className="bg-petroleum text-white hover:bg-petroleum/90">
                      {t("brief_public.send_pitch")}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Link to="/dashboard/proyectos/nuevo" className="ghost-cta rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2">
              {t("brief_public.create_compatible_project")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="mx-auto max-w-6xl px-6 py-16 grid lg:grid-cols-[2fr_1fr] gap-12">
          <div className="space-y-10">
            {brief.description && (
              <div>
                <p className="eyebrow text-gold mb-3">{t("brief_public.description")}</p>
                <p className="text-white/80 leading-relaxed text-lg">{brief.description}</p>
              </div>
            )}

            {(brief.audience_countries?.length || brief.audience_interests?.length) ? (
              <div>
                <p className="eyebrow text-gold mb-3">{t("brief_public.target_audience")}</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <InfoTile label={t("brief_public.ages")} value={brief.audience_age_min && brief.audience_age_max ? `${brief.audience_age_min}–${brief.audience_age_max}` : t("brief_public.dash")} />
                  <InfoTile label={t("brief_public.territories")} value={(brief.audience_countries || []).join(", ") || t("brief_public.dash")} />
                  <InfoTile label={t("brief_public.platforms")} value={(brief.audience_platforms || []).join(", ") || t("brief_public.dash")} />
                </div>
                {brief.audience_interests?.length ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {brief.audience_interests.map((i, k) => <span key={k} className="text-sm px-3 py-1 rounded-full bg-white/5 text-white/80 border border-white/10">{i}</span>)}
                  </div>
                ) : null}
              </div>
            ) : null}

            {brief.contribution_types?.length ? (
              <div>
                <p className="eyebrow text-gold mb-3">{t("brief_public.contribution")}</p>
                <div className="flex flex-wrap gap-2">
                  {brief.contribution_types.map((c, k) => <span key={k} className="text-sm px-3 py-1 rounded-full bg-gold/15 text-gold border border-gold/30 capitalize">{c}</span>)}
                </div>
              </div>
            ) : null}

            {(brief.preferred_formats?.length || brief.preferred_genres?.length || brief.preferred_stages?.length) ? (
              <div>
                <p className="eyebrow text-gold mb-3">{t("brief_public.preferences")}</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <InfoTile label={t("brief_public.formats")} value={(brief.preferred_formats || []).join(", ") || t("brief_public.dash")} />
                  <InfoTile label={t("brief_public.genres")} value={(brief.preferred_genres || []).join(", ") || t("brief_public.dash")} />
                  <InfoTile label={t("brief_public.stages")} value={(brief.preferred_stages || []).join(", ") || t("brief_public.dash")} />
                </div>
              </div>
            ) : null}

            {brief.values?.length ? (
              <div>
                <p className="eyebrow text-gold mb-3">{t("brief_public.values")}</p>
                <div className="flex flex-wrap gap-2">
                  {brief.values.map((v, k) => <span key={k} className="text-sm px-3 py-1 rounded-full bg-white/5 text-white/80 border border-white/10">{v}</span>)}
                </div>
              </div>
            ) : null}
          </div>

          <aside className="space-y-6">
            <div className="glass-card rounded-2xl p-6">
              <p className="eyebrow text-gold mb-3">{t("brief_public.budget")}</p>
              <p className="font-display text-2xl text-white flex items-center gap-2"><DollarSign size={20} className="text-gold" /> {budget}</p>
              {acceptsLine && (
                <p className="text-white/70 text-sm mt-3">{acceptsLine}</p>
              )}
            </div>

            <div className="glass-card rounded-2xl p-6">
              <p className="eyebrow text-gold mb-3">{t("brief_public.territories")}</p>
              <div className="flex flex-wrap gap-2">
                {(brief.territories || []).length === 0
                  ? <span className="text-white/50 text-sm">{t("brief_public.territories_none")}</span>
                  : brief.territories.map((tt, k) => <span key={k} className="text-xs px-3 py-1 rounded-full bg-white/5 text-white/80 border border-white/10">{tt}</span>)}
              </div>
            </div>

            {sponsor && !sponsor.hidden && (
              <div className="glass-card rounded-2xl p-6">
                <p className="eyebrow text-gold mb-3">{t("brief_public.about_sponsor")}</p>
                <p className="font-display text-xl text-white">{sponsor.commercial_name}</p>
                {sponsor.industry && <p className="text-white/60 text-sm mt-1 capitalize">{sponsor.industry}</p>}
                {sponsor.description && <p className="text-white/70 text-sm mt-3 line-clamp-5">{sponsor.description}</p>}
              </div>
            )}
          </aside>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function InfoTile({ label, value }) {
  return (
    <div className="glass-card rounded-2xl p-4">
      <p className="text-xs uppercase tracking-widest text-white/50">{label}</p>
      <p className="text-white mt-1 text-sm">{value}</p>
    </div>
  );
}
